import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";

// OpticSwag cart — client-side (localStorage), scoped to the swag store. Each color+size combo is a
// DISTINCT line (keyed by item+color+size), so a customer can buy multiple variants of the same
// product in one checkout. Adding the same combo again bumps its quantity.
const CartCtx = createContext(null);
const KEY = "os_swag_cart";

export const cartLineKey = (l) => `${l.item_id}|${l.color || ""}|${l.size || ""}`;

export function CartProvider({ children }) {
  const [lines, setLines] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || "[]");
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(lines)); } catch {}
  }, [lines]);

  const add = useCallback((line) => {
    setLines((cur) => {
      const k = cartLineKey(line);
      const max = line.maxQty || 25;
      const idx = cur.findIndex((l) => cartLineKey(l) === k);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: Math.min(max, (next[idx].quantity || 1) + (line.quantity || 1)) };
        return next;
      }
      return [...cur, { ...line, quantity: Math.min(max, line.quantity || 1) }];
    });
  }, []);

  const setQty = useCallback((k, qty) => {
    setLines((cur) => cur.map((l) => (cartLineKey(l) === k
      ? { ...l, quantity: Math.max(1, Math.min(l.maxQty || 25, Number(qty) || 1)) }
      : l)));
  }, []);

  const remove = useCallback((k) => setLines((cur) => cur.filter((l) => cartLineKey(l) !== k)), []);
  const clear = useCallback(() => setLines([]), []);

  const count = useMemo(() => lines.reduce((n, l) => n + (l.quantity || 0), 0), [lines]);
  const subtotalCents = useMemo(() => lines.reduce((n, l) => n + (l.unit_price_cents || 0) * (l.quantity || 0), 0), [lines]);

  return (
    <CartCtx.Provider value={{ lines, add, setQty, remove, clear, count, subtotalCents }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);

// Cart affordance for the swag storefront + product pages: a bag icon with a live item count.
export function CartLink({ className = "" }) {
  const cart = useCart();
  const count = cart?.count || 0;
  return (
    <Link
      to="/swag/cart"
      data-testid="swag-cart-link"
      className={`relative inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-secondary-foreground hover:text-olive-bright transition-colors ${className}`}
    >
      <ShoppingBag className="w-4 h-4" />
      Cart
      {count > 0 && (
        <span
          data-testid="swag-cart-count"
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-olive text-white font-mono text-[10px] font-bold rounded-full"
        >
          {count}
        </span>
      )}
    </Link>
  );
}
